import React, { useMemo, useCallback } from 'react';
import { Button, Card, Col, Row, Space, Typography } from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { Control, FieldErrors, useFieldArray, UseFormSetValue } from 'react-hook-form';
import ModelField from './ModelField';

const { Text } = Typography;

type ArrayGroupProps = {
  field: API.FieldType;
  control: Control<API.defaultValueType>;
  setValue?: UseFormSetValue<API.defaultValueType>;
  errors?: FieldErrors<API.defaultValueType>;
};

const ArrayGroup: React.FC<ArrayGroupProps> = ({ field, control, setValue, errors }) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: field.name,
  });

  // Get default values for new items
  const getDefaultItem = useMemo(() => {
    const defaultItem: any = {};
    field.subFields?.forEach(subField => {
      defaultItem[subField.name] = subField.defaultValue || '';
    });
    return defaultItem;
  }, [field.subFields]);

  const handleAddItem = useCallback(() => {
    append(getDefaultItem);
  }, [append, getDefaultItem]);

  const handleRemoveItem = useCallback((index: number) => {
    remove(index);
  }, [remove]);

  // Get field-level errors
  const fieldErrors = errors?.[field.name] as any;
  const arrayErrors = Array.isArray(fieldErrors) ? fieldErrors : [];


  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Text strong>{field.label}</Text>
        {field.option?.required && <Text type="danger"> *</Text>}
      </div>

      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {fields.map((item, index) => (
          <Card
            key={item.id}
            size="small"
            title={`${field.label} ${index + 1}`}
            extra={
              <Button
                type="text"
                icon={<MinusCircleOutlined />}
                onClick={() => handleRemoveItem(index)}
                disabled={fields.length <= (field.minItems || 1)}
                danger
              >
                Remove
              </Button>
            }
            style={{ marginBottom: 8 }}
          >
            <Row gutter={16}>
              {field.subFields?.map((subField) => (
                <Col key={subField.name} span={subField.grid || 8}>
                  <ModelField
                    field={{
                      ...subField,
                      name: `${field.name}.${index}.${subField.name}`,
                    }}
                    control={control}
                    setValue={setValue}
                    errors={errors}
                  />
                </Col>
              ))}
            </Row>
            {/* Show item-level validation errors */}
            {arrayErrors[index] && (
              <div style={{ marginTop: 8 }}>
                {Object.entries(arrayErrors[index] || {}).map(([fieldName, error]: [string, any]) => {
                  const subField = field.subFields?.find(sf => sf.name === fieldName);
                  return (
                    <Text key={fieldName} type="danger" style={{ display: 'block', fontSize: '12px' }}>
                      {subField?.label || fieldName}: {error?.message || error}
                    </Text>
                  );
                })}
              </div>
            )}
            
          </Card>
        ))}

        {/* Add button */}
        <Button
          type="dashed"
          onClick={handleAddItem}
          icon={<PlusOutlined />}
          style={{ width: '100%' }}
          disabled={field.maxItems ? fields.length >= field.maxItems : false}
        >
          Add {field.label}
        </Button>

        {/* Show array-level validation errors */}
        {fieldErrors && !Array.isArray(fieldErrors) && (
          <Text type="danger" style={{ fontSize: '12px' }}>
            {fieldErrors.message || fieldErrors}
          </Text>
        )}
      </Space>
    </div>
  );
};

export default ArrayGroup;
